import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUserContext } from "../../../contexts/UserContext";
import { getApplicationEntry, KYC_APPLICATION_NAME } from "../../../types/auth";
import DarkModeButtons from "../components/DarkModeButtons";
import LanguageSelectorSettingsPage from "../components/LanguageSelectorSettingsPage";

interface SettingsItem {
  label: string;
  value: ReactNode;
}

/**
 * Settings page — exact copy of the ACT SI Console's Settings page layout
 * (centered 37.5rem column, bordered read-only panels, Language + Dark Mode
 * row), populated with this console's user data.
 */
function SettingsPage() {
  const { userInfo } = useUserContext();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const kycApplication = getApplicationEntry(userInfo, KYC_APPLICATION_NAME);

  const userInfoSection: SettingsItem[] = [
    { label: "Name", value: userInfo?.name },
    { label: "Email", value: userInfo?.email },
  ];

  const companyInfoSection: SettingsItem[] = [
    { label: "Company Name", value: userInfo?.clientName ?? "Advanced Compliance Tech" },
    { label: "Job Title", value: kycApplication?.userTypeName },
    {
      label: "Permissions",
      value: kycApplication?.applicationRoles.join(", ") || kycApplication?.userTypeName,
    },
    { label: "Location", value: userInfo?.location },
  ];

  const groupBoxStyles = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "1rem",
    flexWrap: "wrap",
  } as const;

  const settingsPanelStyles = {
    height: "2.9rem",
    width: "100%",
    minWidth: "18rem",
    border: "0.1rem solid",
    borderRadius: "0.25rem",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "0 0.75rem",
    color: theme.palette.text.primary,
  } as const;

  const subheadStyles = {
    fontSize: "0.85rem",
    fontWeight: "700",
    marginBottom: "0.25rem",
    color: theme.palette.text.primary,
  } as const;

  const SettingsGroup = ({ data }: { data: SettingsItem[] }) => (
    <Box sx={groupBoxStyles}>
      {data.map((item, index) => (
        <Box key={index} sx={{ flex: 1 }}>
          <Typography sx={subheadStyles}>{t(item.label)}</Typography>
          <Box sx={settingsPanelStyles}>{item.value}</Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "70vw",
          maxWidth: "37.5rem",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "1rem" : "4rem",
          margin: "5rem 0 2rem 0",
        }}
      >
        <Typography
          sx={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: theme.palette.text.primary,
          }}
        >
          {t("Settings")}
        </Typography>
        <SettingsGroup data={userInfoSection} />
        <SettingsGroup data={companyInfoSection} />
        <Box sx={groupBoxStyles}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={subheadStyles}>{t("Language")}</Typography>
            <LanguageSelectorSettingsPage />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={subheadStyles}>{t("Dark Mode")}</Typography>
            <DarkModeButtons />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SettingsPage;
